import useWebSocket from 'common/hooks/useWebSocket';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listPatientOrders } from 'store/thunks/patientThunks';

const useLineItemUpdates = (mpi, isActiveTab) => {
  const dispatch = useDispatch();
  const { socket, isConnected } = useWebSocket();
  const { enqueueSnackbar } = useSnackbar();

  const orders = useSelector(({ patient }) => patient[mpi]?.orders || []);
  const currentUserId = useSelector((state) => state.auth?.userId);
  const debounceTimerRef = useRef(null);
  const pendingOrderIdRef = useRef(null);

  const currentLineItemIds = useMemo(() => {
    const lineItemIds = new Set();
    if (orders && Array.isArray(orders)) {
      orders.forEach((order) => {
        if (order.lineItems && Array.isArray(order.lineItems)) {
          order.lineItems.forEach((item) => {
            if (item.lineItemId) {
              lineItemIds.add(item.lineItemId);
            }
          });
        }
      });
    }
    return lineItemIds;
  }, [orders]);

  const handleLineItemUpdate = useCallback(
    (data) => {
      if (!mpi || !isActiveTab) {
        return;
      }

      const eventData = data?.message || data;
      const { lineItemIds, patientId, orderId, userId } = eventData || {};

      if (userId && String(userId) === String(currentUserId)) {
        return;
      }

      if (!patientId || patientId !== mpi) {
        return;
      }

      if (!lineItemIds || !Array.isArray(lineItemIds) || lineItemIds.length === 0) {
        return;
      }

      const hasRelevantLineItem = lineItemIds.some((lineItemId) =>
        currentLineItemIds.has(lineItemId)
      );

      if (hasRelevantLineItem) {
        if (orderId) {
          pendingOrderIdRef.current = orderId;
        }

        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(() => {
          const orderIdToNotify = pendingOrderIdRef.current;
          dispatch(listPatientOrders({ mpi }))
            .then(() => {
              if (orderIdToNotify) {
                enqueueSnackbar(`An Order ${orderIdToNotify} have been modified`, {
                  variant: 'info',
                });
                pendingOrderIdRef.current = null;
              }
            })
            .catch(() => {
              pendingOrderIdRef.current = null;
            });
          debounceTimerRef.current = null;
        }, 5000);
      }
    },
    [mpi, isActiveTab, currentLineItemIds, dispatch, currentUserId, enqueueSnackbar]
  );

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      pendingOrderIdRef.current = null;
    };
  }, [mpi]);

  useEffect(() => {
    if (!socket || !mpi || !isConnected) {
      return undefined;
    }

    const eventHandler = (data) => {
      const eventData = Array.isArray(data) ? data[0] : data;
      handleLineItemUpdate(eventData);
    };

    socket.on('line_item_updated', eventHandler);
    return () => {
      socket.off('line_item_updated', eventHandler);
    };
  }, [socket, mpi, isConnected, handleLineItemUpdate]);
};

export default useLineItemUpdates;
